import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { SUBMISSION_MODE_OPTIONS } from './utils';
import CustomDropdown from 'components/Dropdown/CustomDropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AccessSelection, Dot, SelectedAccess, ToolTipContent } from './styled';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbIcon from 'components/Icons/lightBulbIcon';
import SubmissionModeIcon from 'components/Icons/submissionModeIcon';

const SubmissionMode = (props) => {
  const { data, submissionModeHandler } = props;
  const { SubmissionType: submissionType } = data || {};

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleModeSelection = (item) => {
    submissionModeHandler?.(item.value);
  };

  const handleDropDownOpen = (event: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const renderPermissionItem = useCallback(
    (item): JSX.Element => (
      <Box
        key={item?.value}
        className="permissionItem"
        onClick={() => handleModeSelection(item)}
        sx={{
          backgroundColor:
            submissionType === item?.value ? 'rgba(235, 234, 255, 1)' : 'white',
        }}
      >
        <Typography
          className="title"
          style={{
            color:
              submissionType === item?.value
                ? 'rgba(78, 72, 250, 1)'
                : 'rgba(0, 0, 0, 1)',
          }}
        >
          {item?.label}
        </Typography>
        {item?.description && (
          <Typography className="description">{item?.description}</Typography>
        )}
      </Box>
    ),
    [handleModeSelection, submissionType],
  );

  const selectedPermission = useMemo(() => {
    return SUBMISSION_MODE_OPTIONS?.find((p) => p?.value === submissionType);
  }, [submissionType]);

  return (
    <Stack direction="row" alignItems="center" spacing={2} marginTop="8px">
      <Stack direction="row" alignItems="start" spacing={1} width="100%">
        <SubmissionModeIcon
          sx={{
            height: '30px',
            width: '30px',
            marginTop: '5px',
            marginRight: '13px',
          }}
        />
        <Stack direction="column" width="100%">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              fontSize="14px"
              fontWeight={700}
              color="rgba(33, 33, 33, 1)"
            >
              Submission Mode
            </Typography>
            <Tooltip
              arrow
              placement="right"
              PopperProps={{
                sx: {
                  backgroundColor: 'white',
                  '& .MuiTooltip-tooltip': {
                    borderRadius: '20px !important',
                    backgroundColor: 'white !important',
                    border: '1px solid rgba(238, 238, 238, 1) !important',
                    boxShadow: '0px 4px 8px 0px #49A5CF29 !important',
                    marginLeft: '0px !important',
                  },
                  '& .MuiTooltip-arrow': {
                    color: '#424242 !important',
                    backgroundColor: 'white !important',
                  },
                },
              }}
              title={
                <ToolTipContent>
                  <Typography className="title">Submission Mode</Typography>
                  <Box className="headerAndDescription">
                    <Typography className="subHeader">
                      Control how and where your template can be submitted
                    </Typography>
                    <Typography className="description">
                      This setting determines whether your template can be
                      submitted as a standalone form or only when attached to
                      projects and tasks:
                    </Typography>
                  </Box>

                  <Box className="headerAndDescription">
                    <Typography className="description">
                      <Dot>•</Dot>
                      <span className="subHeader">Both (Default) -</span>{' '}
                      Template available everywhere: standalone submissions from
                      Templates tab/Start button AND can be attached to
                      Projects, Tasks, and Scheduled Work.
                    </Typography>
                  </Box>

                  <Box className="headerAndDescription">
                    <Typography className="description">
                      <Dot>•</Dot>
                      <span className="subHeader">
                        Adhoc Submissions Only -
                      </span>{' '}
                      Template appears only in Templates tab for standalone
                      submissions. Hidden from Project/Task/Scheduled Work
                      attachment options.
                    </Typography>
                  </Box>

                  <Box className="headerAndDescription">
                    <Typography className="description">
                      <Dot>•</Dot>
                      <span className="subHeader">
                        Project-based Submissions Only -
                      </span>{' '}
                      Template only available when attached to Projects, Tasks,
                      or Scheduled Work. Hidden from Templates tab and Start
                      button for standalone use.
                    </Typography>
                  </Box>

                  <Box className="infoBox">
                    <LightbulbIcon className="lightbulbIcon" />
                    <Typography className="description">
                      Use <span className="bold">Project-based</span> mode for
                      templates that will require completion rate reporting,
                      such as a Daily Opening Checklist. Use{' '}
                      <span className="bold">Adhoc</span> mode for templates
                      that occur on an irregular basis, like an Incident
                      Reporting Form.
                    </Typography>
                  </Box>
                </ToolTipContent>
              }
            >
              <InfoOutlinedIcon
                style={{
                  cursor: 'pointer',
                  color: '#616161',
                  marginLeft: 3,
                  height: 18,
                  width: 18,
                }}
              />
            </Tooltip>
          </Stack>
          <Typography
            fontSize="13px"
            color="rgba(33, 33, 33, 1)"
            fontWeight={500}
          >
            {selectedPermission?.description}
          </Typography>

          <CustomDropdown
            onClickawayCallback={() => setAnchorEl(null)}
            buttonRendererProps={{
              style: {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              },
            }}
            buttonRenderer={
              <SelectedAccess onClick={handleDropDownOpen}>
                <Stack
                  direction="row"
                  alignItems="center"
                  width="100%"
                  justifyContent="space-between"
                >
                  <Typography className="selectedLabel">
                    {selectedPermission?.label ?? 'Select Submission Mode'}
                  </Typography>
                  <KeyboardArrowDownIcon className="dropdownIcon" />
                </Stack>
              </SelectedAccess>
            }
            popperProps={{
              open: Boolean(anchorEl),
              anchorEl,
              placement: 'bottom-start',
              sx: { width: 480, borderRadius: 2, boxShadow: 3 },
              content: (
                <AccessSelection>
                  {SUBMISSION_MODE_OPTIONS.map(renderPermissionItem)}
                </AccessSelection>
              ),
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SubmissionMode;
